import { useState, useEffect } from "react";
import { Navigate, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { useTranslation } from "../i18n/hooks/useTranslation";
import UserMenu from "../components/common/UserMenu";
import PageMenu from "../components/common/PageMenu";
import Logo from "../components/common/Logo";
import { adminMenuItems } from "../constants/menuItems";
import Footer from "../components/common/Footer";
import AdminShortcutHelpModal from "../components/common/AdminShortcutHelpModal";
import ProfileCompletionBanner from "../components/common/ProfileCompletionBanner";
import MaintenanceBanner from "../components/common/MaintenanceBanner";
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import {
  IconMenu2, IconX, IconLogout,
  IconLayoutDashboard, IconTemplate, IconPrinter, IconPhoto, IconWand, IconTags, IconCategory,
  IconBriefcase, IconSun, IconMoon, IconLanguage, IconKeyboard, IconSettings,
} from '@tabler/icons-react';

function AdminLayout() {
  const { isAuthenticated, user } = useAuthStore();
  const { t } = useTranslation();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  if (!isAuthenticated || !user) {
    const redirect = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirect)}`} replace />;
  }

  if (user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  const isImageGenPage = location.pathname.startsWith("/admin/generate-image");
  // Template galleries/lists + the AI create/edit form use a full-width layout
  // (no max-width cap) — a masonry grid beside a filter sidebar needs the whole
  // screen, and the print-template list is the same shape as the AI one. Other
  // pages in these sections (e.g. the negative-feedback table) keep the normal
  // capped container.
  const isFullWidthPage =
    location.pathname === "/admin/ai-templates" ||
    location.pathname === "/admin/ai-templates/new" ||
    /^\/admin\/ai-templates\/[^/]+\/edit$/.test(location.pathname) ||
    location.pathname === "/admin/print-templates";
  const isPrintStudio =
    location.pathname === "/admin/print-templates/new" ||
    /^\/admin\/print-templates\/[^/]+\/edit$/.test(location.pathname);

  // Immersive Figma-style studio — no chrome around the canvas editor
  if (isPrintStudio) {
    return (
      <div className="fixed inset-0 z-[80] bg-white dark:bg-zinc-950 overflow-hidden">
        <Outlet />
        <AdminShortcutHelpModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} showTrigger={false} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">

      {/* Full-width header */}
      <header className="main-nav-wrapper w-full sticky nav:static top-0 z-[60] nav:z-auto bg-white dark:bg-zinc-950 nav:border-b nav:border-zinc-200 dark:border-zinc-700 px-4 sm:px-6 xl:px-8">
        {/* ── Service maintenance notice (toggle via SHOW flag in the component) ── */}
        {/* <MaintenanceBanner /> */}

        {/* Contest banner intentionally omitted for admins. */}

        {/* ── Desktop nav (≥860px) — single row ── */}
        <div className="hidden nav:block font-hind">
          <div className="relative">
            <div className="flex items-center gap-3 xl:gap-4 py-2.5">
              {/* Brand logo + Admin label */}
              <div className="flex items-center gap-[10px] shrink-0 mr-1">
                <Link to="/" className="flex items-center">
                  <Logo w={120} />
                </Link>
                <span className="text-zinc-400 dark:text-zinc-500">|</span>
                <h3 className="text-lg font-bold text-[#FF9900] whitespace-nowrap">
                  {t("navigation.admin")}
                </h3>
              </div>
              {/* Nav menu fills the middle. "সব মেনু" renders as the trailing tab. */}
              <PageMenu
                menuItems={adminMenuItems}
                trailing={
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(true)}
                    aria-label={t('navigation.allMenu')}
                    className="flex items-center gap-1 min-[1100px]:gap-1.5 whitespace-nowrap text-[12.5px] min-[1100px]:text-[13.5px] font-medium px-1.5 min-[1100px]:px-2.5 py-2 rounded-lg text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors focus:outline-none"
                  >
                    <IconMenu2 size={16} className="shrink-0" />
                    {t('navigation.allMenu')}
                  </button>
                }
              />
              {/* Right: profile (theme / language / shortcuts live inside the menu) */}
              <div className="flex items-center gap-2 shrink-0">
                <UserMenu />
              </div>
            </div>
            {/* Full-width megamenu slot — capped to the container width. */}
            <div id="sohoz-megamenu-slot" className="absolute left-0 right-0 top-full z-[200] max-w-container mx-auto" />
          </div>
          {/* Slim profile-completion strip (renders only when incomplete; hidden for admins) */}
          <ProfileCompletionBanner />
        </div>

        {/* ── Mobile / tablet top bar (<860px) ── */}
        <div className="nav:hidden flex items-center justify-between py-2.5 font-hind">
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center">
              <Logo w={110} />
            </Link>
            <span className="text-zinc-400 dark:text-zinc-500 text-sm">|</span>
            <span className="text-base font-bold text-[#FF9900]">{t("navigation.admin")}</span>
          </div>

          {/* Right: সব মেনু + profile dropdown */}
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label={t('navigation.allMenu')}
              className="flex items-center gap-1.5 h-9 px-2.5 rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors focus:outline-none"
            >
              <IconMenu2 size={18} stroke={1.8} />
              <span className="hidden sm:inline text-[13px] font-medium">{t('navigation.allMenu')}</span>
            </button>
            <UserMenu />
          </div>
        </div>
      </header>

      <div className={`${isFullWidthPage ? 'max-w-none' : 'max-w-container'} mx-auto px-4 sm:px-6 w-full flex flex-col flex-1`}>
        <div className={`flex-1 ${isImageGenPage ? '' : 'mt-8'}`}>
          <Outlet />
        </div>
        <div className="hidden nav:block"><Footer /></div>
      </div>

      {/* ── Left Drawer (সব মেনু) — works on mobile and desktop ── */}
      <AdminMobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpenShortcuts={() => { setDrawerOpen(false); setShortcutsOpen(true); }}
      />

      {/* Keyboard shortcuts modal — lifted to root so it survives the drawer closing */}
      <AdminShortcutHelpModal open={shortcutsOpen} onOpenChange={setShortcutsOpen} showTrigger={false} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Admin "সব মেনু" drawer — mirrors the user sidebar design, keeps
// every admin-specific menu item.
// ─────────────────────────────────────────────────────────────
function AdminMobileDrawer({ open, onClose, onOpenShortcuts }) {
  const { t, currentLanguage, changeLanguage } = useTranslation();
  const isBn = currentLanguage === 'bn';
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  // Theme toggle (mirrors ColorSchemeToggle) — surfaced as a labeled row below.
  const [isDark, setIsDark] = useState(() =>
    localStorage.getItem('theme') === 'dark' ||
    (!localStorage.getItem('theme') && document.documentElement.classList.contains('dark'))
  );
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
      root.setAttribute('data-mantine-color-scheme', 'dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      root.setAttribute('data-mantine-color-scheme', 'light');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
  };

  const handleLogout = () => { onClose(); logout(); navigate('/login'); };
  const handleProfileClick = () => { onClose(); navigate('/admin/profile'); };

  // All admin menus (flattened — AI templates + categories are separate rows).
  const navItems = [
    { path: '/admin',                        labelKey: 'navigation.dashboard',            Icon: IconLayoutDashboard },
    { path: '/admin/template-sets',          labelKey: 'navigation.documentSets',         Icon: IconTemplate },
    { path: '/admin/print-templates',        labelKey: 'navigation.printTemplates',       Icon: IconPrinter },
    { path: '/admin/generate-image',         labelKey: 'navigation.generateImageAI',      Icon: IconPhoto },
    { path: '/admin/generate-image-manual',  labelKey: 'navigation.generateImageManual',  Icon: IconPhoto },
    { path: '/admin/ai-templates',           labelKey: 'navigation.aiTemplateTemplates',  Icon: IconWand },
    { path: '/admin/ai-template-categories', labelKey: 'navigation.aiTemplateCategories', Icon: IconWand },
    { path: '/admin/jobs',                   labelKey: 'navigation.adminJobs',            Icon: IconBriefcase },
    { path: '/admin/tags',                   labelKey: 'navigation.tags',                 Icon: IconTags },
    { path: '/admin/categories',             labelKey: 'navigation.categories',           Icon: IconCategory },
  ];

  const isActive = (path) => path === '/admin'
    ? location.pathname === '/admin'
    : location.pathname === path || location.pathname.startsWith(path + '/');

  const renderNavItem = (item) => {
    const active = isActive(item.path);
    const label = t(item.labelKey) || item.labelKey.split('.').pop();
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={onClose}
        className={cn(
          'flex items-center gap-[13px] px-[13px] py-[11px] rounded-[10px] text-[14px] transition-colors',
          active
            ? 'bg-primary/10 dark:bg-primary/20 text-[#1C1B19] dark:text-zinc-100 font-semibold'
            : 'text-[#56524A] dark:text-zinc-300 font-medium hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70'
        )}
      >
        <item.Icon
          size={18}
          stroke={1.7}
          className={cn('shrink-0', active ? 'text-primary' : 'text-[#9C988F] dark:text-zinc-500')}
        />
        <span className="flex-1 truncate text-[14px]">{label}</span>
      </Link>
    );
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <SheetContent
        side="left"
        className="p-0 flex flex-col h-full w-[308px] max-w-[85vw] z-[99999] font-hind bg-[#FBFAF8] dark:bg-zinc-900 border-r border-[#ECE9E3] dark:border-zinc-800"
        showClose={false}
      >
        <SheetTitle className="sr-only">{t('navigation.allMenu')}</SheetTitle>

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-[18px] py-4 border-b border-[#ECE9E3] dark:border-zinc-800">
          <div className="flex items-center gap-2">
            <Link to="/" onClick={onClose} className="flex items-center">
              <Logo w={112} />
            </Link>
            <span className="text-zinc-400 dark:text-zinc-500 text-sm">|</span>
            <span className="text-[15px] font-bold text-[#FF9900]">{t("navigation.admin")}</span>
          </div>
          <button
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
            className="w-[34px] h-[34px] rounded-[9px] border border-[#ECE9E3] dark:border-zinc-700 bg-white dark:bg-zinc-800 flex items-center justify-center text-[#6E6A63] dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors shrink-0"
          >
            <IconX size={16} stroke={2} />
          </button>
        </div>

        {/* ── Nav ── */}
        <div className="flex-1 overflow-y-auto px-[14px] pt-[14px] pb-[18px]">
          <p className="text-[13px] font-bold text-[#A8A294] dark:text-zinc-500 uppercase tracking-[0.05em] px-[13px] pt-1 pb-2">
            {t('navigation.adminPanel')}
          </p>
          <div className="flex flex-col gap-0.5">
            {navItems.map(renderNavItem)}
          </div>

          <div className="h-px bg-[#F1EFEA] dark:bg-zinc-800 mx-[13px] my-3" />

          {/* Section: অন্যান্য (preferences) */}
          <p className="text-[13px] font-bold text-[#A8A294] dark:text-zinc-500 uppercase tracking-[0.05em] px-[13px] pt-1 pb-2">
            {t('navigation.sectionOther')}
          </p>
          <div className="flex flex-col gap-0.5">
            {/* Dark mode */}
            <button
              onClick={() => setIsDark((v) => !v)}
              className="w-full flex items-center gap-[13px] px-[13px] py-[11px] rounded-[10px] text-[14px] font-medium text-[#56524A] dark:text-zinc-300 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
            >
              {isDark
                ? <IconSun size={18} stroke={1.7} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
                : <IconMoon size={18} stroke={1.7} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />}
              <span className="flex-1 text-left truncate text-[14px]">{isBn ? 'ডার্ক মোড' : 'Dark Mode'}</span>
              <span className="flex items-center border border-[#ECE9E3] dark:border-zinc-700 rounded-md overflow-hidden shrink-0">
                <span className={cn('flex items-center px-2 py-1', !isDark ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900' : 'text-zinc-400')}>
                  <IconSun size={13} />
                </span>
                <span className={cn('flex items-center px-2 py-1', isDark ? 'bg-zinc-900 text-white dark:bg-primary dark:text-white' : 'text-zinc-400')}>
                  <IconMoon size={13} />
                </span>
              </span>
            </button>

            {/* Language */}
            <button
              onClick={() => changeLanguage(isBn ? 'en' : 'bn')}
              className="w-full flex items-center gap-[13px] px-[13px] py-[11px] rounded-[10px] text-[14px] font-medium text-[#56524A] dark:text-zinc-300 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
            >
              <IconLanguage size={18} stroke={1.7} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
              <span className="flex-1 text-left truncate text-[14px]">{t('navigation.language') || 'ভাষা'}</span>
              <span className="flex items-center border border-[#ECE9E3] dark:border-zinc-700 rounded-md overflow-hidden text-[12px] font-semibold shrink-0">
                <span className={cn('px-2 py-0.5', !isBn ? 'bg-primary text-white' : 'text-zinc-400')}>EN</span>
                <span className={cn('px-2 py-0.5', isBn ? 'bg-primary text-white' : 'text-zinc-400')}>বাং</span>
              </span>
            </button>

            {/* Keyboard shortcuts */}
            <button
              onClick={() => onOpenShortcuts?.()}
              className="w-full flex items-center gap-[13px] px-[13px] py-[11px] rounded-[10px] text-[14px] font-medium text-[#56524A] dark:text-zinc-300 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
            >
              <IconKeyboard size={18} stroke={1.7} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
              <span className="flex-1 text-left truncate text-[14px]">{t('shortcuts.admin.keyboardShortcuts') || 'কীবোর্ড শর্টকাট'}</span>
            </button>

            {/* Settings */}
            <Link
              to="/admin/settings"
              onClick={onClose}
              className="flex items-center gap-[13px] px-[13px] py-[11px] rounded-[10px] text-[14px] font-medium text-[#56524A] dark:text-zinc-300 hover:bg-[#F2EDE7] dark:hover:bg-zinc-800/70 transition-colors"
            >
              <IconSettings size={18} stroke={1.7} className="text-[#9C988F] dark:text-zinc-500 shrink-0" />
              <span className="flex-1 truncate text-[14px]">{t('settings.title') || 'সেটিংস'}</span>
            </Link>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="border-t border-[#ECE9E3] dark:border-zinc-800 px-[18px] py-3">
          <div className="flex items-center gap-[11px]">
            <button
              onClick={handleProfileClick}
              className="flex items-center gap-[11px] flex-1 min-w-0 text-left"
            >
              <span className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-amber-400 flex items-center justify-center text-white font-semibold text-[14px] shrink-0 overflow-hidden">
                {user?.profilePicture ? (
                  <img src={user.profilePicture} alt={user?.name || 'User'} className="w-full h-full object-cover" />
                ) : (
                  getInitials(user?.name)
                )}
              </span>
              <span className="min-w-0 leading-tight">
                <span className="block text-[15px] font-semibold text-[#1C1B19] dark:text-zinc-100 truncate">
                  {user?.name || user?.email || 'User'}
                </span>
                <span className="block text-[13px] text-[#A8A294] dark:text-zinc-500 font-mono truncate">
                  {user?.phone || user?.email}
                </span>
              </span>
            </button>
            <button
              onClick={handleLogout}
              aria-label={t('navigation.logout')}
              title={t('navigation.logout')}
              className="w-9 h-9 rounded-[9px] border border-[#ECE9E3] dark:border-zinc-700 bg-white dark:bg-zinc-800 flex items-center justify-center text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors shrink-0"
            >
              <IconLogout size={16} />
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default AdminLayout;
